import { Navigate } from 'react-router-dom';
import { auth } from '../firebase';
import { useState, useEffect, useCallback, useRef } from 'react';
import useIsMobile from '../utils/mediaQuery';
import { Button, Progress, message } from 'antd';
import { DownloadOutlined, CheckCircleFilled } from '@ant-design/icons';
import { useGetApkMetaQuery } from '../features/api/apiSlice';
import { openDB } from 'idb';
import logo from '../assets/logo.png';
import checkAuthOnLoad from '../features/checkAuth';
import './ApkDownloadGate.css';

const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB per Range-request chunk
const CONCURRENCY = 3;
const MAX_RETRIES = 5;
const RETRY_BASE_MS = 1500;
const IDB_NAME = 'apk-download';
const IDB_STORE = 'chunks';
const IDB_META = 'meta';

// ── Mobile device detection (cannot be bypassed via "Desktop site" or landscape) ──

function isMobileDevice() {
  if (typeof navigator === 'undefined') return false;
  if (navigator.userAgentData?.mobile) return true;
  if (navigator.maxTouchPoints > 0) {
    const ua = (navigator.userAgent || '').toLowerCase();
    if (/android|iphone|ipad|ipod|mobile|tablet/.test(ua)) return true;
  }
  const shortest = Math.min(window.screen.width, window.screen.height);
  if (shortest <= 480 && navigator.maxTouchPoints > 0) return true;
  return false;
}

// ── Helpers ──────────────────────────────────────────────────────────

function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

// ── IndexedDB helpers (chunked download persistence) ────────────────

async function getDb() {
  return openDB(IDB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
      if (!db.objectStoreNames.contains(IDB_META)) {
        db.createObjectStore(IDB_META);
      }
    },
  });
}

async function saveChunk(db, index, data) {
  await db.put(IDB_STORE, data, index);
}

async function getDownloadMeta(db) {
  return db.get(IDB_META, 'current');
}

async function setDownloadMeta(db, meta) {
  await db.put(IDB_META, meta, 'current');
}

async function getCompletedChunks(db) {
  const keys = await db.getAllKeys(IDB_STORE);
  return new Set(keys);
}

async function assembleAndDownload(db, meta) {
  const parts = [];
  for (let i = 0; i < meta.totalChunks; i++) {
    const chunk = await db.get(IDB_STORE, i);
    if (!chunk) throw new Error(`Missing chunk ${i}`);
    parts.push(chunk);
  }
  const blob = new Blob(parts, { type: 'application/vnd.android.package-archive' });
  triggerBlobDownload(blob, meta.fileName);
  await clearDownload(db);
}

async function clearDownload(db) {
  const tx = db.transaction([IDB_STORE, IDB_META], 'readwrite');
  await tx.objectStore(IDB_STORE).clear();
  await tx.objectStore(IDB_META).delete('current');
  await tx.done;
}

function triggerBlobDownload(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 120_000);
}

// ── Service Worker registration ─────────────────────────────────────

async function registerDownloadSW() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/apk-download-sw.js');
    if (reg.active) return reg;
    const sw = reg.installing || reg.waiting;
    if (!sw) return reg;
    return new Promise((resolve) => {
      sw.addEventListener('statechange', function handler() {
        if (sw.state === 'activated') {
          sw.removeEventListener('statechange', handler);
          resolve(reg);
        }
      });
    });
  } catch (e) {
    console.warn('SW registration failed:', e);
    return null;
  }
}

// ── Chunked Range-request downloader ────────────────────────────────

async function fetchChunkWithRetry(url, start, end, retries = MAX_RETRIES) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(url, {
        headers: { Range: `bytes=${start}-${end}` },
      });
      if (res.status === 206 || res.ok) {
        return await res.arrayBuffer();
      }
      throw new Error(`HTTP ${res.status}`);
    } catch (err) {
      if (attempt === retries) throw err;
      await sleep(RETRY_BASE_MS * 2 ** attempt);
    }
  }
}

/**
 * Probe Range support with a 1-byte GET (S3 often blocks CORS HEAD / omits Accept-Ranges).
 */
async function probeRangeSupport(url, knownSize = 0) {
  try {
    const rangeRes = await fetch(url, {
      headers: { Range: 'bytes=0-0' },
    });
    if (rangeRes.status === 206) {
      const cr = rangeRes.headers.get('Content-Range') || '';
      const match = cr.match(/\/(\d+)/);
      const total = match ? parseInt(match[1], 10) : knownSize;
      // Drain tiny body so the connection can close cleanly
      await rangeRes.arrayBuffer().catch(() => {});
      return { supported: true, totalSize: total || knownSize };
    }
    await rangeRes.arrayBuffer().catch(() => {});
    return { supported: false, totalSize: knownSize };
  } catch {
    return { supported: false, totalSize: knownSize };
  }
}

/** Single-request stream with byte progress (when Range/IDB resume is unavailable). */
async function streamDownloadWithProgress(url, fileName, knownSize, onProgress, signal) {
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const total = knownSize || parseInt(res.headers.get('Content-Length') || '0', 10) || 0;

  if (!res.body || typeof res.body.getReader !== 'function') {
    const blob = await res.blob();
    onProgress(blob.size, total || blob.size);
    triggerBlobDownload(blob, fileName);
    return;
  }

  const reader = res.body.getReader();
  const parts = [];
  let received = 0;
  onProgress(0, total);

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    parts.push(value);
    received += value.byteLength;
    onProgress(received, total || received);
  }

  const blob = new Blob(parts, {
    type: 'application/vnd.android.package-archive',
  });
  triggerBlobDownload(blob, fileName);
}

function formatProgressLabel(downloaded, total) {
  if (total > 0) {
    const pct = Math.min(100, Math.round((downloaded / total) * 100));
    return `${formatBytes(downloaded)} / ${formatBytes(total)} (${pct}%)`;
  }
  if (downloaded > 0) return `${formatBytes(downloaded)} downloaded`;
  return 'Preparing download…';
}

// ── Component ───────────────────────────────────────────────────────

export default function AuthRoute({ children }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const isMobile = useIsMobile();
  const isNativeWebView = typeof window.ReactNativeWebView !== 'undefined';
  const isMobileDeviceFlag = isMobileDevice();

  // Download state
  const [downloadState, setDownloadState] = useState('idle');
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadedBytes, setDownloadedBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [statusText, setStatusText] = useState('');

  const swRegistered = useRef(false);
  const downloadAbort = useRef(null);

  const showDownloadScreen = isMobileDeviceFlag && !isNativeWebView;

  const { data: apkMeta } = useGetApkMetaQuery(undefined, {
    skip: !user || !showDownloadScreen,
  });

  useEffect(() => {
    const checkAuth = async () => {
      try {
        auth.onAuthStateChanged(async (firebaseUser) => {
          if (firebaseUser) {
            try {
              await firebaseUser.getIdToken(true);
              setUser(firebaseUser);
            } catch (error) {
              console.error('Error refreshing Firebase token:', error);
              setUser(null);
            }
          } else {
            await checkAuthOnLoad(setLoading, null, setUser);
          }
          setLoading(false);
        });
      } catch (error) {
        console.error('Error during authentication:', error);
        setUser(null);
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  // Register SW, listen for BG Fetch messages, check for resumable download
  useEffect(() => {
    if (!showDownloadScreen || !user) return;
    if (swRegistered.current) return;

    let cancelled = false;

    (async () => {
      await registerDownloadSW();
      if (cancelled) return;
      swRegistered.current = true;

      // Check for an in-progress Background Fetch
      navigator.serviceWorker?.controller?.postMessage({ type: 'CHECK_BG_FETCH' });

      // Check for a resumable chunked download in IDB
      try {
        const db = await getDb();
        const meta = await getDownloadMeta(db);
        if (meta) {
          // Discard stale chunks from an older APK version
          const currentVersion = apkMeta?.version;
          if (currentVersion && meta.version && meta.version !== currentVersion) {
            await clearDownload(db);
          } else {
            const completed = await getCompletedChunks(db);
            if (completed.size >= meta.totalChunks) {
              setDownloadState('complete');
              setDownloadProgress(100);
              setStatusText('Download complete — tap to save');
              assembleAndDownload(db, meta);
            } else if (completed.size > 0) {
              const bytes = completed.size * CHUNK_SIZE;
              setDownloadState('resumable');
              setDownloadedBytes(bytes);
              setTotalBytes(meta.totalSize);
              setDownloadProgress(Math.round((bytes / meta.totalSize) * 100));
              setStatusText(`${completed.size}/${meta.totalChunks} chunks saved — tap Resume`);
            }
          }
        }
      } catch (e) {
        console.warn('IDB check failed:', e);
      }
    })();

    const onMessage = (event) => {
      if (cancelled) return;
      const d = event.data || {};
      switch (d.type) {
        case 'BG_FETCH_STARTED':
          setDownloadState('downloading');
          setStatusText('Downloading via Background Fetch — safe to switch apps');
          break;
        case 'BG_FETCH_STATUS':
          if (d.active) {
            setDownloadState('downloading');
            setDownloadedBytes(d.downloaded || 0);
            setTotalBytes(d.total || 0);
            if (d.total > 0) {
              setDownloadProgress(Math.round((d.downloaded / d.total) * 100));
            }
            setStatusText(formatProgressLabel(d.downloaded || 0, d.total || 0));
          }
          break;
        case 'DOWNLOAD_PROGRESS':
          setDownloadState('downloading');
          setDownloadedBytes(d.downloaded || 0);
          setTotalBytes(d.total || 0);
          if (d.total > 0) {
            setDownloadProgress(Math.round((d.downloaded / d.total) * 100));
          }
          setStatusText(formatProgressLabel(d.downloaded || 0, d.total || 0));
          break;
        case 'DOWNLOAD_COMPLETE':
          setDownloadState('complete');
          setDownloadProgress(100);
          setStatusText('Download complete!');
          if (d.source === 'bg-fetch') {
            caches
              .open('apk-v1')
              .then((c) => c.match('/__apk__'))
              .then((r) => {
                if (!r) return;
                return r.blob().then((blob) => {
                  triggerBlobDownload(blob, `Inframent-${apkMeta?.version || 'latest'}.apk`);
                  caches.open('apk-v1').then((c) => c.delete('/__apk__'));
                });
              });
          }
          break;
        case 'DOWNLOAD_ERROR':
          setDownloadState('error');
          setStatusText(d.error || 'Download failed');
          message.error('Download failed: ' + (d.error || 'unknown error'));
          break;
        default:
          break;
      }
    };

    navigator.serviceWorker?.addEventListener('message', onMessage);
    return () => {
      cancelled = true;
      navigator.serviceWorker?.removeEventListener('message', onMessage);
    };
  }, [showDownloadScreen, user, apkMeta]);

  // Chunked download with IDB persistence (fallback when BG Fetch unavailable)
  const startChunkedDownload = useCallback(
    async (url, fileName, fileSize, version, resume = false) => {
      const abortController = new AbortController();
      downloadAbort.current = abortController;

      try {
        const db = await getDb();
        let totalSize = fileSize;
        let totalChunks;
        let completed;

        if (resume) {
          const meta = await getDownloadMeta(db);
          if (meta) {
            totalSize = meta.totalSize;
            totalChunks = meta.totalChunks;
            completed = await getCompletedChunks(db);
          } else {
            resume = false;
          }
        }

        if (!resume) {
          const probe = await probeRangeSupport(url, fileSize);
          if (!probe.supported) {
            // Stream with live byte progress (no silent <a> fallback).
            setDownloadState('downloading');
            setTotalBytes(fileSize);
            setStatusText('Downloading…');
            await streamDownloadWithProgress(
              url,
              fileName,
              fileSize,
              (downloaded, total) => {
                setDownloadedBytes(downloaded);
                setTotalBytes(total);
                if (total > 0) {
                  setDownloadProgress(Math.min(100, Math.round((downloaded / total) * 100)));
                }
                setStatusText(formatProgressLabel(downloaded, total));
              },
              abortController.signal
            );
            if (abortController.signal.aborted) return;
            setDownloadProgress(100);
            setDownloadState('complete');
            setStatusText('Download complete!');
            return;
          }
          totalSize = probe.totalSize || fileSize;
          totalChunks = Math.ceil(totalSize / CHUNK_SIZE);
          completed = new Set();
          await clearDownload(db);
          await setDownloadMeta(db, {
            totalSize,
            totalChunks,
            fileName,
            url,
            version,
          });
        }

        setDownloadState('downloading');
        setTotalBytes(totalSize);
        setStatusText('Downloading — progress is saved if you leave this page');

        let completedCount = completed.size;
        const pending = [];
        for (let i = 0; i < totalChunks; i++) {
          if (!completed.has(i)) pending.push(i);
        }

        const updateProgress = () => {
          const bytes = Math.min(completedCount * CHUNK_SIZE, totalSize);
          setDownloadedBytes(bytes);
          const pct = totalSize > 0 ? Math.round((bytes / totalSize) * 100) : 0;
          setDownloadProgress(pct);
          setStatusText(formatProgressLabel(bytes, totalSize));
        };
        updateProgress();

        let nextIdx = 0;
        const worker = async () => {
          while (nextIdx < pending.length) {
            if (abortController.signal.aborted) return;
            const idx = nextIdx++;
            const chunkIndex = pending[idx];
            const start = chunkIndex * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE - 1, totalSize - 1);

            const data = await fetchChunkWithRetry(url, start, end);
            if (abortController.signal.aborted) return;

            await saveChunk(db, chunkIndex, data);
            completedCount++;
            updateProgress();
          }
        };

        await Promise.all(
          Array.from({ length: Math.min(CONCURRENCY, pending.length) }, () => worker())
        );

        if (abortController.signal.aborted) return;

        setStatusText('Assembling file…');
        setDownloadProgress(100);
        const meta = await getDownloadMeta(db);
        await assembleAndDownload(db, meta);
        setDownloadState('complete');
        setStatusText('Download complete!');
      } catch (err) {
        if (abortController.signal.aborted) return;
        console.error('Chunked download error:', err);
        setDownloadState('error');
        setStatusText('Download interrupted — tap Retry to resume');
        message.error('Download interrupted. Progress was saved — tap Retry to resume.');
      } finally {
        downloadAbort.current = null;
      }
    },
    []
  );

  const handleDownload = useCallback(async () => {
    const url = apkMeta?.download_url;
    if (!url) {
      message.error('Download is not ready yet. Please try again in a moment.');
      return;
    }
    const fileName = apkMeta?.fileName || `Inframent-${apkMeta?.version || 'latest'}.apk`;
    const fileSize = apkMeta?.size || 0;
    const version = apkMeta?.version || null;

    // If resumable, continue from where we left off
    if (downloadState === 'resumable' || downloadState === 'error') {
      setDownloadState('downloading');
      startChunkedDownload(url, fileName, fileSize, version, true);
      return;
    }

    // Try Background Fetch via SW first
    if (navigator.serviceWorker?.controller) {
      setDownloadState('downloading');
      setDownloadProgress(0);
      setDownloadedBytes(0);
      setTotalBytes(fileSize);
      setStatusText('Starting download...');

      // Ask SW to try Background Fetch; listen for BG_FETCH_UNSUPPORTED
      const bgFetchPromise = new Promise((resolve) => {
        const handler = (event) => {
          const t = event.data?.type;
          if (t === 'BG_FETCH_STARTED') {
            navigator.serviceWorker.removeEventListener('message', handler);
            resolve('started');
          } else if (t === 'BG_FETCH_UNSUPPORTED') {
            navigator.serviceWorker.removeEventListener('message', handler);
            resolve('unsupported');
          }
        };
        navigator.serviceWorker.addEventListener('message', handler);
        setTimeout(() => {
          navigator.serviceWorker.removeEventListener('message', handler);
          resolve('timeout');
        }, 3000);
      });

      navigator.serviceWorker.controller.postMessage({
        type: 'START_DOWNLOAD',
        url,
        fileName,
        totalSize: fileSize,
      });

      const result = await bgFetchPromise;
      if (result === 'started') return; // BG Fetch is running, SW handles the rest

      // BG Fetch unsupported or timed out — fall back to chunked download
    }

    // Chunked Range-request download with IDB persistence
    startChunkedDownload(url, fileName, fileSize, version, false);
  }, [apkMeta, downloadState, startChunkedDownload]);

  if (loading) return null;
  if (!user) return <Navigate to="/auth/sign-in" replace />;

  if (showDownloadScreen) {
    const isDownloading = downloadState === 'downloading';
    const isComplete = downloadState === 'complete';
    const isError = downloadState === 'error';
    const isResumable = downloadState === 'resumable';
    const showProgress = isDownloading || isResumable || isError;
    const progressLabel = formatProgressLabel(downloadedBytes, totalBytes);

    let hint = 'Progress is saved — you can leave and resume later.';
    if (isDownloading) {
      hint = 'Keep this tab open until the file finishes saving.';
    } else if (isResumable) {
      hint = 'Partial download saved — resume when ready.';
    } else if (isError) {
      hint = statusText || 'Something interrupted the download. Retry to continue.';
    } else if (isComplete) {
      hint = null;
    } else if (
      statusText &&
      !statusText.startsWith('Download complete') &&
      statusText !== progressLabel
    ) {
      hint = statusText;
    }

    return (
      <div className="apk-gate">
        <div className="apk-gate__card">
          <img className="apk-gate__logo" src={logo} alt="Inframent" />
          <h1 className="apk-gate__title">Inframent</h1>
          {apkMeta ? (
            <p className="apk-gate__meta">
              Version {apkMeta.version} · {formatBytes(apkMeta.size)}
            </p>
          ) : (
            <p className="apk-gate__meta">Android app install</p>
          )}

          <div className="apk-gate__body">
            {showProgress && (
              <>
                <p
                  className={`apk-gate__status${
                    isError
                      ? ' apk-gate__status--error'
                      : isResumable
                        ? ' apk-gate__status--warn'
                        : ''
                  }`}
                >
                  {isError
                    ? 'Download paused'
                    : isResumable
                      ? `Ready to resume · ${downloadProgress}%`
                      : `Downloading · ${downloadProgress}%`}
                </p>
                <Progress
                  className="apk-gate__progress"
                  percent={downloadProgress}
                  status={isError || isResumable ? 'exception' : 'active'}
                  showInfo={false}
                  strokeColor={
                    isError || isResumable
                      ? { from: '#d97706', to: '#f59e0b' }
                      : { from: '#1aa6b7', to: '#2f8f5b' }
                  }
                />
                <p className="apk-gate__bytes">{progressLabel}</p>
              </>
            )}

            {isComplete && (
              <div className="apk-gate__success">
                <div className="apk-gate__check" aria-hidden>
                  <CheckCircleFilled />
                </div>
                <p className="apk-gate__success-title">Ready to install</p>
                <p className="apk-gate__success-copy">
                  The APK is in your Downloads folder. Open it to install Inframent.
                </p>
              </div>
            )}
          </div>

          <div className="apk-gate__actions">
            {!isDownloading && !isComplete && (
              <Button
                className="apk-gate__btn apk-gate__btn--primary"
                type="primary"
                size="large"
                icon={<DownloadOutlined />}
                onClick={handleDownload}
              >
                {isResumable ? 'Resume download' : isError ? 'Retry download' : 'Download APK'}
              </Button>
            )}

            {isComplete && (
              <Button
                className="apk-gate__btn apk-gate__btn--ghost"
                size="large"
                icon={<DownloadOutlined />}
                onClick={handleDownload}
              >
                Download again
              </Button>
            )}
          </div>

          {hint ? <p className="apk-gate__hint">{hint}</p> : null}
        </div>
      </div>
    );
  }

  return children;
}
